<template>
  <section class="cw-flex cw-flex-col cw-flex-grow cw-overflow-y-hidden cw-border cw-border-gray-200"
    role="tabpanel">
    <find-my-location-input :mapbox-config="mapboxConfig"
      @select="handleFeatureSelection" />

    <!-- Selected Location Details Card -->
    <div v-if="selectedOrigin.center"
      class="cw-flex cw-justify-between cw-rounded cw-break-words cw-border cw-bg-white cw-border-gray-300 cw-mx-3 cw-p-2 cw-gap-2 cw-shadow-md"
      role="group">
      <!-- Selected Location Details -->
      <div class="cw-flex cw-flex-col">
        <!-- Address Details -->
        <component :is="index === 0 ? 'h5' : 'p'"
          v-for="(addressDetail, index) in selectedOrigin.place_name.split(',')"
          :key="addressDetail"
          :class="index === 0 ? 'cw-text-sm' : 'cw-text-xs'">
          {{ addressDetail.trim() }}
        </component>

        <!-- Closes Enabled Location -->
        <p class="cw-w-full cw-mt-3 cw-text-xs">
          {{ $t('enabled-locations-map.closest-mp-enabled-location') }}
          <strong>{{ closestEnabledLocation.name }}</strong>
        </p>
      </div>
      <!-- Map Pin -->
      <img src="@/locations-map/assets/home-pin.png"
        alt="map pin"
        class="cw-h-10">
      <!-- Marker Element for Map -->
      <div ref="marker"
        class="marker"
        role="none" />
    </div>

    <!-- Locations by Distance -->
    <template v-if="enabledFeaturesByDistance.length">
      <h2 class="cw-font-bold cw-px-3 cw-py-2 cw-mt-2">
        {{ $t('enabled-locations-map.locations-by-distance') }}
      </h2>

      <div ref="locationsListing"
        class="cw-overflow-y-scroll cw-border-t cw-border-gray-200"
        role="list">
        <location-item v-for="location in enabledFeaturesByDistance"
          :key="location.id"
          :location="location"
          :selected-location="selectedLocation"
          @selectLocation="handleSelectLocation">
          <!-- Mobile: Selected Location Map Snapshot -->
          <div v-if="location.id === selectedLocation.id && isMobileDevice"
            class="cw-h-[300px] cw-w-full cw-mb-5">
            <mapbox-static :mapbox-config="mapboxConfig"
              :selected-location="selectedLocation" />
          </div>
        </location-item>
      </div>
    </template>
  </section>
</template>

<script>
import Mapbox from 'mapbox-gl'
import FindMyLocationInput from './FindMyLocationInput'
import LocationItem from '@/locations-map/components/common/LocationItem'
import MapboxStatic from '@/locations-map/components/common/MapboxStatic'
import { dispatchCustomEvent } from '@/locations-map/components/common/MapHelpers'

export default {
  name: 'FindMyLocation',

  components: {
    'find-my-location-input': FindMyLocationInput,
    'location-item': LocationItem,
    'mapbox-static': MapboxStatic,
  },

  props: {
    mapContext: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    mapboxConfig: {
      type: Object,
      required: true,
      default: () => ({}),

    },
    enabledLocations: {
      type: Array,
      required: true,
      default: () => [],
    },
    isMobileDevice: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      selectedOrigin: {},
      selectedLocation: {},
    }
  },

  computed: {
    enabledLocationsDistances() {
      if (!this.selectedOrigin.center) return []
      return this.enabledLocations.map(location => {
        const dest = new Mapbox.LngLat(location.longitude, location.latitude)
        const dist = this.selectedOrigin.lngLat.distanceTo(dest)
        return { distance: dist, ...location }
      })
    },
    enabledFeaturesByDistance() {
      if (!this.enabledLocationsDistances.length) return []
      return this.enabledLocationsDistances
        .filter(location => location.status === 'Active')
        .slice()
        .sort((a, b) => {
          if (a.distance > b.distance) return 1
          if (a.distance < b.distance) return -1
          return 0
        })
    },
    closestEnabledLocation() {
      if (!this.enabledFeaturesByDistance.length) return { dc: {} }
      return this.enabledFeaturesByDistance[0]
    },
    boundsArray() {
      if (!this.enabledFeaturesByDistance.length) return []
      return [
        [this.selectedOrigin.lngLat.lng, this.selectedOrigin.lngLat.lat],
        [this.selectedLocation.longitude, this.selectedLocation.latitude],
      ]
    },
    bounds() {
      if (!this.boundsArray.length) return []
      return new Mapbox.LngLatBounds(this.boundsArray).toArray()
    },
  },

  methods: {
    async handleFeatureSelection(feature) {
      const lngLat = new Mapbox.LngLat(feature.center[0], feature.center[1])
      this.selectedOrigin = {
        ...feature,
        lngLat: lngLat,
      }
      this.selectedLocation = this.closestEnabledLocation
      await this.$nextTick()
      if (!this.isMobileDevice) this.dropHomePin()
      this.drawLineEvents()
    },
    dropHomePin() {
      this.marker = new Mapbox.Marker(this.$refs.marker).setLngLat(this.selectedOrigin.center).addTo(this.mapContext)
    },
    async handleSelectLocation(location) {
      this.selectedLocation = location
      await this.$nextTick()
      this.drawLineEvents()
    },
    drawLineEvents() {
      dispatchCustomEvent('mp_drawLine', { points: this.boundsArray })
      dispatchCustomEvent('mp_zoomToBounds', {
        bounds: this.bounds,
        zoom: 20,
        showLocationPopup: true,
        location: this.selectedLocation,
      })
      dispatchCustomEvent('mp_showLocationPopup', { showLocationPopup: true, location: this.selectedLocation })
    },
  },
}
</script>

<style>
.marker {
  background: url("~@/locations-map/assets/home-pin.png") no-repeat;
  background-size: contain;
  width: 22px;
  height: 44px;
}
</style>
